import React, { useState } from 'react'
import { PlatinumData } from '../../types/carbon/models/PlatinumData'
import { TableViewSettingsData } from '../../types/app'
import { GridCell, platinumToGrid } from './layout'

/**
 * PlatinumTableDebug Component
 *
 * A debug table that faithfully renders the PlatinumData grid structure
 * and allows inspection of server-provided coordinates and metadata.
 *
 * Features:
 * - Click cells to inspect their coordinates and metadata
 * - Clear borders around all cells
 * - Coordinate overlays for groups
 * - Info panel showing selected cell details
 */

interface PlatinumTableProps {
  data: PlatinumData;
  viewSettings?: TableViewSettingsData;
  className?: string;
}

interface SelectedCellInfo {
  cell: GridCell;
  rowIdx: number;
  colIdx: number;
}

export const PlatinumTableDebug: React.FC<PlatinumTableProps> = ({
  data,
  viewSettings,
  className = ''
}) => {
  const grid = platinumToGrid(data, true) // debug = true: preserve exact server coordinates
  const [selectedCell, setSelectedCell] = useState<SelectedCellInfo | null>(null)

  // Get cell background color based on type
  const getCellBackgroundColor = (cell: GridCell): string => {
    switch (cell.type) {
      case 'group': return '#f8d6e5'
      case 'label': return '#b6c0fa'
      case 'letter': return '#d6f5e3'
      case 'data': return '#fff5e6'
      case 'empty': return '#f9fafb'
      default: return '#efefef'
    }
  }

  // Handle cell click
  const handleCellClick = (cell: GridCell, rowIdx: number, colIdx: number) => {
    if (cell.type === 'occupied') {
      return // Don't select occupied cells
    }
    setSelectedCell({ cell, rowIdx, colIdx })

    // Also log to console for easier debugging
    console.log('Cell clicked:', {
      type: cell.type,
      content: cell.content,
      gridPosition: { rowIdx, colIdx },
      serverCoordinates: { x: cell.x, y: cell.y, w: cell.w, h: cell.h },
      rowspan: cell.rowspan,
      colspan: cell.colspan,
      sourceData: cell.sourceData,
      bandStyle: cell.bandStyle,
      hierarchy: cell.hierarchy,
      position: cell.position
    })
  }

  return (
    <div
      className={className}
      style={{
        padding: '20px',
        overflow: 'auto',
        width: '100%',
        height: '100%',
        backgroundColor: '#f8f9fa',
        display: 'flex',
        gap: '20px'
      }}
    >
      {/* Table Container */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <table style={{
          borderCollapse: 'separate',
          borderSpacing: 0,
          fontFamily: 'monospace',
          fontSize: '11px',
          backgroundColor: 'white',
          border: '2px solid #000'
        }}>
          <tbody>
            {grid.map((row, rowIdx) => (
              <tr key={rowIdx}>
                {row.map((cell, colIdx) => {
                  if (cell.type === 'occupied') {
                    return null
                  }

                  const isSelected = selectedCell?.rowIdx === rowIdx && selectedCell?.colIdx === colIdx
                  const backgroundColor = getCellBackgroundColor(cell)

                  return (
                    <td
                      key={colIdx}
                      rowSpan={cell.rowspan}
                      colSpan={cell.colspan}
                      onClick={() => handleCellClick(cell, rowIdx, colIdx)}
                      style={{
                        border: '1px solid #999',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        position: 'relative',
                        minWidth: '70px',
                        maxWidth: '120px',
                        minHeight: '35px',
                        padding: '4px',
                        backgroundColor: isSelected ? '#ffeb3b' : backgroundColor,
                        cursor: 'pointer',
                        boxShadow: isSelected ? 'inset 0 0 0 3px #ff9800' : 'none'
                      }}
                    >
                      {/* Coordinate overlay for groups */}
                      {cell.type === 'group' && (
                        <div style={{
                          position: 'absolute',
                          top: '2px',
                          left: '2px',
                          fontSize: '8px',
                          color: '#666',
                          backgroundColor: 'rgba(255,255,255,0.8)',
                          padding: '1px 3px',
                          borderRadius: '2px',
                          fontWeight: 'bold'
                        }}>
                          [{cell.x},{cell.y}] {cell.w}x{cell.h}
                        </div>
                      )}

                      {/* Cell content */}
                      <div style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        marginTop: cell.type === 'group' ? '12px' : '0'
                      }}>
                        {cell.content || '\u00A0'}
                      </div>

                      {/* Type badge */}
                      <div style={{
                        position: 'absolute',
                        bottom: '2px',
                        right: '2px',
                        fontSize: '7px',
                        color: '#999',
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        padding: '1px 2px',
                        borderRadius: '2px'
                      }}>
                        {cell.type.substring(0, 1).toUpperCase()}
                      </div>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Info Panel */}
      <div style={{
        width: '350px',
        backgroundColor: 'white',
        border: '2px solid #333',
        borderRadius: '4px',
        padding: '15px',
        fontFamily: 'monospace',
        fontSize: '12px',
        overflow: 'auto',
        maxHeight: 'calc(100vh - 100px)'
      }}>
        <h3 style={{ margin: '0 0 15px 0', fontSize: '14px', fontWeight: 'bold' }}>
          Cell Inspector
        </h3>

        {selectedCell ? (
          <div>
            <div style={{ marginBottom: '10px' }}>
              <strong>Cell Type:</strong>
              <div style={{
                display: 'inline-block',
                marginLeft: '10px',
                padding: '2px 8px',
                backgroundColor: getCellBackgroundColor(selectedCell.cell),
                border: '1px solid #999',
                borderRadius: '3px'
              }}>
                {selectedCell.cell.type}
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong>Grid Position:</strong>
              <div style={{ marginLeft: '10px', marginTop: '5px' }}>
                Row: {selectedCell.rowIdx}, Col: {selectedCell.colIdx}
              </div>
            </div>

            <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
              <strong>Server Coordinates:</strong>
              <div style={{ marginLeft: '10px', marginTop: '5px' }}>
                <div>X (column): {selectedCell.cell.x ?? 'N/A'}</div>
                <div>Y (row): {selectedCell.cell.y ?? 'N/A'}</div>
                <div>W (width): {selectedCell.cell.w ?? 'N/A'}</div>
                <div>H (height): {selectedCell.cell.h ?? 'N/A'}</div>
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong>HTML Attributes:</strong>
              <div style={{ marginLeft: '10px', marginTop: '5px' }}>
                <div>rowSpan: {selectedCell.cell.rowspan}</div>
                <div>colSpan: {selectedCell.cell.colspan}</div>
              </div>
            </div>

            {selectedCell.cell.content && (
              <div style={{ marginBottom: '15px' }}>
                <strong>Content:</strong>
                <div style={{ marginLeft: '10px', marginTop: '5px', wordBreak: 'break-word' }}>
                  "{selectedCell.cell.content}"
                </div>
              </div>
            )}

            {selectedCell.cell.sourceData && (
              <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#e8f5e9', borderRadius: '4px' }}>
                <strong>Data Values:</strong>
                <div style={{ marginLeft: '10px', marginTop: '5px' }}>
                  <div>Frequency: {selectedCell.cell.sourceData.freq}</div>
                  <div>Col %: {selectedCell.cell.sourceData.colpc}</div>
                  <div>Row %: {selectedCell.cell.sourceData.rowpc}</div>
                  <div>Significance: {selectedCell.cell.sourceData.sig || '(none)'}</div>
                </div>
              </div>
            )}

            {selectedCell.cell.bandStyle && (
              <div style={{ marginBottom: '15px' }}>
                <strong>Band Info:</strong>
                <div style={{ marginLeft: '10px', marginTop: '5px' }}>
                  <div>Vec Type: {selectedCell.cell.bandStyle.vecType}</div>
                  <div>Band: {selectedCell.cell.bandStyle.band}</div>
                </div>
              </div>
            )}

            {selectedCell.cell.hierarchy && (
              <div style={{ marginBottom: '15px' }}>
                <strong>Hierarchy:</strong>
                <div style={{ marginLeft: '10px', marginTop: '5px' }}>
                  <div>Top Vec: {selectedCell.cell.hierarchy.topVecIndex}</div>
                  <div>Side Vec: {selectedCell.cell.hierarchy.sideVecIndex}</div>
                  <div>Band: {selectedCell.cell.hierarchy.bandIndex}</div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ color: '#999', textAlign: 'center', marginTop: '50px' }}>
            Click any cell to inspect its properties
          </div>
        )}
      </div>
    </div>
  )
}

export default PlatinumTableDebug